import React from "react";

// External
import * as d3 from "d3";

// Local
import {
  countryRegionColor,
  BoxRow,
  BoxColumn,
  colorScheme
} from "../../styled/styled";
import CountryDot from "./CountryDot";
import LegendItem from "./LegendItem";
import ViewControls from "./ViewControls";
import Voronoi from "./Voronoi";
import Tooltip from "./Tooltip";
import Clusters from "./Clusters";
import CountryLine from "./CountryLine";

const Plot = ({ state, data, actions, dim }) => {
  const { containerWidth } = dim;
  const margin = { top: 24, right: 24, bottom: 24, left: 24 };
  const plotWidth = containerWidth - margin.left - margin.right;
  const plotHeight = 0.65 * plotWidth; // 0.7 * windowHeight;
  const { countryData, countryRegSubreg, recordMatrix } = data;
  const plotDim = { plotWidth, plotHeight };
  const {
    currentData,
    currentStepValue,
    currentProjection,
    tooltip,
    organization,
    cluster,
    projection
  } = state;

  const regions = [...new Set(countryData.map(ctry => ctry.region))].sort();
  const membershipValues = countryRegSubreg.map(ctry => ctry.membershipCount);

  // Max & Min Values
  const geoX = currentData.map(ctry => ctry.x_geo);
  const geoY = currentData.map(ctry => ctry.y_geo);
  const tsneX = currentData.map(ctry => ctry.x_tsne);
  const tsneY = currentData.map(ctry => ctry.y_tsne);

  const geoXMin = d3.min(geoX);
  const geoXMax = d3.max(geoX);
  const geoYMin = d3.min(geoY);
  const geoYMax = d3.max(geoY);

  const tsneXMin = d3.min(tsneX);
  const tsneXMax = d3.max(tsneX);
  const tsneYMin = d3.min(tsneY);
  const tsneYMax = d3.max(tsneY);

  const membershipMin = d3.min(membershipValues);
  const membershipMax = d3.max(membershipValues);

  const orgCountMax = d3.max(recordMatrix.map(mat => mat.orgCount));
  const orgCountMin = d3.min(recordMatrix.map(mat => mat.orgCount));

  // Scales
  const geoXScale = d3
    .scaleLinear()
    .domain([geoXMin, geoXMax])
    .range([margin.left, plotWidth - margin.right]);
  const geoYScale = d3
    .scaleLinear()
    .domain([geoYMin, geoYMax])
    .range([margin.top, plotHeight - margin.top]);
  const tsneXScale = d3
    .scaleLinear()
    .domain([tsneXMin, tsneXMax])
    .range([margin.left, plotWidth - margin.right]);
  const tsneYScale = d3
    .scaleLinear()
    .domain([tsneYMin, tsneYMax])
    .range([plotHeight - margin.top, margin.top]);

  const membershipScale = d3
    .scaleSqrt()
    .domain([membershipMin, membershipMax])
    .range([0, containerWidth * 0.008]);

  const lineOpacityScale = d3
    .scalePow()
    .exponent(2)
    .domain([orgCountMin, orgCountMax])
    .range([0, 1]);

  const lineThicknessScale = d3
    .scalePow()
    .exponent(1)
    .domain([orgCountMin, orgCountMax])
    .range([0, 7]);

  return (
    <>
      <div>
        {currentProjection === "geo" ? (
          <h4>Countries according to their relative geographical position</h4>
        ) : (
          <h4>
            Countries based on a{" "}
            <a
              href="https://distill.pub/2016/misread-tsne/"
              target="_blank"
              rel="noopener noreferrer"
            >
              clustering algorithm
            </a>
          </h4>
        )}
      </div>
      {tooltip.country && (
        <Tooltip tooltip={tooltip} dim={{ plotWidth, plotHeight }} />
      )}
      <svg
        height={plotHeight}
        width={plotWidth}
        // preserveAspectRatio="xMidYMid meet"
      >
        {/* For debugging purposes only */}
        {/* <rect width="100%" height="100%" fill="magenta" /> */}
        <g className="circle">
          {currentData.map((ctry, i) => (
            <CountryDot
              key={`circle${i}`}
              className="countryDots"
              cx={
                currentProjection === "geo"
                  ? geoXScale(ctry.x_geo)
                  : tsneXScale(ctry.x_tsne)
              }
              cy={
                currentProjection === "geo"
                  ? geoYScale(ctry.y_geo)
                  : tsneYScale(ctry.y_tsne)
              }
              r={membershipScale(
                countryRegSubreg.find(c => c.country === ctry.country)
                  .membershipCount
              )}
              stroke={countryRegionColor(ctry.region)}
              strokeWidth={"2px"}
              fill={
                organization.hovered
                  ? organization.secondaryMembers.some(
                      c => c.countryName === ctry.country
                    )
                    ? "transparent"
                    : countryRegionColor(ctry.region)
                  : countryRegionColor(ctry.region)
              }
              opacity={
                organization.hovered
                  ? organization.allMembers.some(
                      c => c.countryName === ctry.country
                    )
                    ? 0.9
                    : 0.1
                  : 0.9
              }
              strokeLocation={"inside"}
            />
          ))}
        </g>
        <Clusters
          plotDim={plotDim}
          dim={dim}
          margin={margin}
          cluster={cluster}
          currentProjection={currentProjection}
        />
        <CountryLine
          currentProjection={currentProjection}
          recordMatrix={recordMatrix}
          data={currentData}
          scale={{
            geoXScale,
            geoYScale,
            tsneXScale,
            tsneYScale,
            lineOpacityScale,
            lineThicknessScale
          }}
          tooltip={tooltip}
        />
        <Voronoi
          dim={dim}
          margin={margin}
          currentProjection={currentProjection}
          data={currentData}
          scale={{ geoXScale, geoYScale, tsneXScale, tsneYScale }}
          actions={actions}
        />
      </svg>
      <BoxRow style={{ justifyContent: "space-between" }}>
        <ViewControls
          actions={actions}
          currentStepValue={currentStepValue}
          currentProjection={currentProjection}
          projection={projection}
        />
        <BoxColumn>
          <BoxRow>
            {regions.map((reg, i) => (
              <LegendItem
                key={`reg${i}`}
                label={reg}
                fill={countryRegionColor(reg)}
              />
            ))}
          </BoxRow>
          <BoxRow style={{ justifyContent: "flex-end" }}>
            <LegendItem
              key={`primaryMember`}
              label={`member`}
              fill={colorScheme.secondaryColor}
            />
            <LegendItem
              key={`secondaryMember`}
              label={`observer, associate member and others`}
              fill={"none"}
              stroke={colorScheme.neutralLightColor}
              strokeWidth={2}
            />
          </BoxRow>
        </BoxColumn>
      </BoxRow>
    </>
  );
};

export default Plot;
